import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Clock, Zap } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Loader, EmptyState, StatusBadge } from "@/components/common";
import { INSTALL_STATUS } from "@/lib/labels";
import { PageTitle, Panel } from "./AdminShell";

export default function AdminInstallPlanning() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/installations").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loader />;

  const scheduled = items.filter((i) => i.scheduled_date && !["cloturee"].includes(i.status));
  // group by date -> zone
  const byDate = {};
  scheduled.forEach((i) => {
    byDate[i.scheduled_date] = byDate[i.scheduled_date] || {};
    (byDate[i.scheduled_date][i.zone] = byDate[i.scheduled_date][i.zone] || []).push(i);
  });
  const dates = Object.keys(byDate).sort();
  const urgent = items.filter((i) => i.urgency === "urgent" && !["cloturee", "terminee"].includes(i.status));

  return (
    <div>
      <PageTitle>Planning des installations</PageTitle>

      {urgent.length > 0 && (
        <Panel className="p-4 mb-5 border-red-200 bg-red-50">
          <p className="font-bold text-red-700 flex items-center gap-2 mb-2"><Zap className="w-4 h-4" /> Interventions urgentes ({urgent.length})</p>
          <div className="space-y-1">
            {urgent.map((i) => (
              <div key={i.id} className="text-sm text-red-800 flex flex-wrap gap-2" data-testid={`urgent-${i.id}`}>
                <span className="font-num font-bold">{i.mission_number}</span> · {i.product_name} · {i.customer_name} · {i.zone}
                {i.professional_name ? <span className="text-red-600">→ {i.professional_name}</span> : <span className="font-semibold">(non affectée)</span>}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {dates.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Aucune installation programmée" subtitle="Les interventions avec une date planifiée apparaîtront ici, regroupées par jour et par zone." />
      ) : dates.map((date) => (
        <div key={date} className="mb-6" data-testid={`planning-date-${date}`}>
          <h2 className="font-display text-xl font-bold text-[#0F172A] mb-2 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#C85A32]" /> {formatDate(date)}
          </h2>
          {Object.entries(byDate[date]).map(([zone, list]) => (
            <div key={zone} className="mb-3">
              <p className="text-sm font-bold text-[#C85A32] mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {zone}</p>
              <Panel className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                    <th className="text-left px-4 py-2">Heure</th><th className="text-left px-4 py-2">Client</th>
                    <th className="text-left px-4 py-2">Intervention</th><th className="text-left px-4 py-2">Professionnel</th>
                    <th className="text-left px-4 py-2">Statut</th>
                  </tr></thead>
                  <tbody>
                    {list.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")).map((i) => (
                      <tr key={i.id} className="border-t border-slate-100" data-testid={`planning-row-${i.id}`}>
                        <td className="px-4 py-2 font-num font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> {i.scheduled_time || "—"}</td>
                        <td className="px-4 py-2">{i.customer_name}</td>
                        <td className="px-4 py-2">{i.product_name} <span className="text-xs text-slate-400">({i.skill})</span></td>
                        <td className="px-4 py-2">{i.professional_name || <span className="text-amber-600 font-semibold">Non affecté</span>}</td>
                        <td className="px-4 py-2"><StatusBadge map={INSTALL_STATUS} value={i.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
